/**
 * SupportChatMount — #3105 (#3096-E).
 *
 * Thin wrapper around `SupportChatWidget` that owns:
 *   - the FAB / drawer-trigger button,
 *   - open/close local state,
 *   - the login-required gate (per founder decision #3096-1).
 *
 * Two embed surfaces:
 *
 *   - `embed="fab"`     — used by `AppFleet` on the marketing site. A floating
 *                         bottom-right pill button reveals the 380×560 panel.
 *                         Renders nothing when the visitor is signed-out.
 *
 *   - `embed="drawer"`  — used by `FleetLayout` inside the authenticated SPA.
 *                         A small "Support" pill in the header toggles a
 *                         right-side 420 px drawer. `FleetLayout` only renders
 *                         children for signed-in users via `Protected`, so the
 *                         login gate here is belt-and-braces.
 *
 * The widget itself (`SupportChatWidget`) does not own the trigger, so the
 * mount is also responsible for the visible "open support" affordance. The
 * widget is mounted lazily (open=false until the user clicks) so the
 * createConversation POST never fires for visitors who never engage.
 *
 * Reserved-words hygiene (#3154): no top-level `type` / `role` / `model` /
 * `version` / `data` fields anywhere in this module — the props we forward
 * use `embed`, `referrerOrigin`, `agent_role` (passthrough).
 */
import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { SupportChatWidget } from "./SupportChatWidget";
import type { SupportEmbedMode } from "./SupportChatWidget";
import type { SupportEmbedOrigin } from "../../api/supportChatApi";

/**
 * Routes covered by the authenticated `FleetLayout` chrome. The marketing
 * FAB suppresses itself on these because `FleetLayout` already exposes the
 * drawer trigger — we never want both visible at once.
 *
 * Mirrors the union of paths that `FleetLayout.isFleetRoute` + the
 * `getHiveRoutes` / `getAccountRoutes` / `getAdminRoutes` modules wrap in
 * `<Protected>` / `<PlatformAdminProtected>`. The list is short enough to
 * keep inline; extracting it would force a circular import with FleetLayout
 * for one boolean.
 */
const ADMIN_CHROME_PREFIXES: readonly string[] = [
  "/fleet",
  "/hives",
  "/all-hives",
  "/servers",
  "/audit",
  "/integrations",
  "/profile",
  "/account",
  "/billing",
  "/danger-zone",
  "/usage",
  "/upgrade",
  "/support",            // customer ticket history (#2444)
  "/admin",              // /admin/cost-dashboard, /admin/support, /admin/templates, …
];

function isAdminChromeRoute(p: string): boolean {
  return ADMIN_CHROME_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + "/"));
}

export interface SupportChatMountProps {
  /** Embed surface — `fab` for marketing, `drawer` for the admin SPA. */
  embed:           SupportEmbedMode;
  /** Maps to `referrer_origin` on the conversation row (spec §3.1). */
  referrerOrigin:  SupportEmbedOrigin;
}

export function SupportChatMount({ embed, referrerOrigin }: SupportChatMountProps) {
  // Login-required per founder decision #3096-1 (2026-06-03). useAuth() is
  // safe to call unconditionally — both embeds are mounted inside AuthProvider.
  const { token } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onClose = useCallback(() => setOpen(false), []);
  const onOpen  = useCallback(() => setOpen(true), []);

  // Hide entirely for unauthenticated visitors — no FAB, no drawer button.
  // (The widget refuses to bootstrap without auth anyway because the POST
  // /v1/support/conversations would 401; we just skip rendering the trigger.)
  if (!token) return null;

  // The marketing FAB is App-level so its <Routes/> siblings can't gate it.
  // Suppress it on routes wrapped in FleetLayout — those already expose the
  // drawer trigger and showing two affordances would be confusing.
  if (embed === "fab" && isAdminChromeRoute(location.pathname)) return null;

  return (
    <>
      {/* Open trigger — visually distinct per embed mode. */}
      {!open && (
        <button
          data-testid={`support-chat-trigger-${embed}`}
          onClick={onOpen}
          aria-label="Open 2hives support chat"
          style={
            embed === "fab"
              ? {
                  position: "fixed",
                  bottom: 16,
                  right: 16,
                  zIndex: 8500,
                  background: "linear-gradient(135deg, var(--brand-status-success), var(--brand-primary))",
                  color: "var(--brand-bg-deep)",
                  border: "none",
                  borderRadius: 24,
                  padding: "10px 18px",
                  fontFamily: "monospace",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
                  letterSpacing: "0.04em",
                }
              : {
                  // Drawer trigger is a compact header-style pill — caller
                  // (FleetLayout) drops it into the topbar so it sits next to
                  // the other utility buttons (Cmd-K, theme toggle).
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "transparent",
                  border: "1px solid var(--brand-border-subtle)",
                  borderRadius: 4,
                  padding: "4px 8px",
                  color: "var(--brand-text-secondary)",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "0.72rem",
                }
          }
        >
          {embed === "fab" ? "💬 Support" : "Support"}
        </button>
      )}

      <SupportChatWidget
        embed={embed}
        referrerOrigin={referrerOrigin}
        open={open}
        onClose={onClose}
        jwt={token}
      />
    </>
  );
}
